import React, { useEffect, useState } from 'react'
import style from './cardEmployee.module.scss'
import { IPeriod, IProjectToEmployeeLink } from '../../starter/model/interfaces'
import { useDrag } from 'react-dnd'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '../../starter/constants'
import { Progress, Tooltip } from 'antd'
import PersonContactSet from '../PersonContactSet/PersonContactSet'
import TextArea from 'antd/es/input/TextArea'
import {
  useCreateProjectEmployeeLinksMutation,
  useUpdateProjectEmployeeLinksMutation
} from '../../starter/api/projectEmployeeLinksApi'
import CardEmployeeTooltip from '../CardEmployeeSmall/CardEmployeeTooltip'
import { useAppSelector } from '../../starter/hooks/redux'
import CreatePeriod from '../CreatePeriod/CreatePeriod'
import { useActions } from '../../starter/hooks/actions'
import { store } from '../../starter/redux/store'

type TCardEmployee = {
  linkItem: IProjectToEmployeeLink
  isSmall: boolean
}
export default function CardEmployee({ linkItem }: TCardEmployee) {
  const [barPercent, setBarPercent] = useState<number>(0)
  const [activePeriods, setActivePeriods] = useState<IPeriod[]>([])
  const [currentPeriod, setCurrentPeriod] = useState<IPeriod>()
  const [oldPeriod, setOldPeriod] = useState<IPeriod>()
  const [futurePeriod, setFuturePeriod] = useState<IPeriod>()
  const [currentPeriodType, setCurrentTypePeriod] = useState<'old' | 'current' | 'future'>()
  const { onShowModal, setButtonSet, setModalData, setTmpPeriod } = useActions()

  const [updateLink, { isSuccess: isSuccessUpdate }] = useUpdateProjectEmployeeLinksMutation()
  const [createLink, { isSuccess: isSuccessCreate }] = useCreateProjectEmployeeLinksMutation()

  const [tmpLink, setTmpLink] = useState<IProjectToEmployeeLink>({ ...linkItem })

  const [commentRows, setCommentRows] = useState<number>(2)
  const [commentAutosize, setCommentAutosize] = useState<boolean | any>(false)

  const { isSmall } = useAppSelector((state) => state.viewOptions.main)

  useEffect(() => {
    setActivePeriods(linkItem.periods.filter((period) => !period.isHidden))
  }, [linkItem.periods])

  useEffect(() => {
    if (isSuccessUpdate || isSuccessCreate) {
      onShowModal(false)
    }
  }, [isSuccessUpdate, isSuccessCreate])

  useEffect(() => {
    const _oldPeriod = activePeriods.filter((p) => dayjs(p.endTime) < dayjs()).reverse()[0]
    const _actual = activePeriods.find((p) => dayjs(p.startTime) <= dayjs() && dayjs() <= dayjs(p.endTime))
    const _futurePeriod = activePeriods.find((p) => dayjs() < dayjs(p.endTime))
    setOldPeriod(_oldPeriod)
    setCurrentPeriod(_actual)
    setFuturePeriod(_futurePeriod)
    if (_actual) {
      setCurrentTypePeriod('current')
    } else if (_oldPeriod) {
      setCurrentTypePeriod('old')
    } else if (!_futurePeriod) {
      setCurrentTypePeriod('future')
    }
  }, [activePeriods])

  useEffect(() => {
    switch (currentPeriodType) {
      case 'current': {
        if (currentPeriod) {
          const allPeriodLength = dayjs(currentPeriod.endTime).diff(dayjs(currentPeriod.startTime), 'days')
          const currentPeriodLength = dayjs().diff(dayjs(currentPeriod.startTime), 'days')
          const percent = Math.round((100 * currentPeriodLength) / allPeriodLength)
          setBarPercent(percent)
        }
        break
      }
      case 'future': {
        setBarPercent(0)
        break
      }
      case 'old': {
        setBarPercent(101)
        break
      }
    }
  }, [currentPeriod, currentPeriodType])

  const addLink = (linkItem: IProjectToEmployeeLink) => {
    const { period: tmpPeriod, doRemovePeriods } = { ...store.getState()?.tmp }
    if (tmpPeriod || doRemovePeriods) {
      const _linkItem: IProjectToEmployeeLink = {
        ...linkItem,
        periods: tmpPeriod
          ? [...linkItem.periods.filter((p) => !doRemovePeriods.map((t) => t.id).includes(p.id)), tmpPeriod]
          : [...linkItem.periods.filter((p) => !doRemovePeriods.map((t) => t.id).includes(p.id))]
      }
      if (!linkItem._id) {
        createLink(_linkItem)
      } else {
        updateLink(_linkItem)
      }
    }
    setTmpPeriod(null)
  }

  const showPeriods = (e: React.MouseEvent) => {
    e.stopPropagation()
    onShowModal(true)
    setButtonSet([
      {
        type: 'primary',
        onClick: () => addLink(linkItem),
        children: 'Ok'
      },
      {
        onClick: () => onShowModal(false),
        children: 'Cancel'
      }
    ])
    setModalData({
      title: 'установите период привлечения сотрудника',
      content: <CreatePeriod onChange={setTmpPeriod} periods={linkItem ? linkItem.periods : []} showRange={true} />
    })
  }

  const [, employeeRef] = useDrag({
    type: 'employee',
    item: linkItem,
    collect: (monitor) => monitor
  })

  const calcBarColor = (percent: Number) => {
    if (percent > 0 && percent < 75) return 'green'
    if (percent >= 75 && percent <= 100) return '#ead503'
    if (percent > 100) return 'red'
  }

  const handleInputChange = (name: string, value: string): void => {
    setTmpLink({
      ...tmpLink,
      [name]: value
    })
  }

  const commentFocusHandler = () => {
    setCommentRows(6)
    setCommentAutosize({ minRows: 2, maxRows: 6 })
  }
  const commentBlurHandler = () => {
    updateLink(tmpLink)
    setCommentRows(2)
    setCommentAutosize(false)
  }

  return (
    <div
      className={`${style.wrapper} ${currentPeriodType === 'old' && style.oldPeriod} ${currentPeriodType === 'future' && style.futurePeriod}`}
      ref={employeeRef}
      onDoubleClick={showPeriods}>
      <Tooltip
        overlayInnerStyle={{ width: '35rem' }}
        title={() => <CardEmployeeTooltip employee={linkItem.employee} />}
        trigger={'click'}>
        <span className={style.title}>
          {linkItem.employee.firstName} {linkItem.employee.lastName}
        </span>
      </Tooltip>
      {isSmall && (
        <Progress
          percent={barPercent}
          showInfo={barPercent < 100 && true}
          strokeColor={calcBarColor(barPercent)}
          size='small'
        />
      )}
      {!isSmall && (
        <>
          <div className={style.info}>
            <PersonContactSet person={linkItem.employee} />
            <div className={style.endTime}>
              {currentPeriodType === 'current' && currentPeriod && dayjs(currentPeriod.endTime).format(DATE_FORMAT)}
              {currentPeriodType === 'future' && futurePeriod && dayjs(futurePeriod.startTime).format(DATE_FORMAT)}
              {currentPeriodType === 'old' && oldPeriod && dayjs(oldPeriod.endTime).format(DATE_FORMAT)}
            </div>
          </div>

          <div className={style.progressBar}>
            <Progress percent={barPercent} showInfo={barPercent < 100 && true} strokeColor={calcBarColor(barPercent)} />
          </div>
          <div className={style.comment}>
            <TextArea
              rows={commentRows}
              onFocus={commentFocusHandler}
              onBlur={commentBlurHandler}
              autoSize={commentAutosize}
              value={tmpLink.comment}
              name='comment'
              style={{ lineHeight: 1.2 }}
              onChange={(e) => {
                handleInputChange('comment', e.target.value)
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}
